/**
 * MarkerReportButton.jsx — iter899 (Feb 2026)
 *
 * Small "🚩 Докладвай" button + modal for flagging a suspect marker
 * value in `lab_entries`. Used inside Twin pages and the BHI
 * Auto-Diagnosis panel.
 *
 * Props:
 *   markerCode         (required) — canonical code (e.g. "dhea_s")
 *   markerLabel        (optional) — human label to prefill
 *   currentValue       (optional)
 *   currentUnit        (optional)
 *   labEntryId         (optional) — for direct row lookup
 *   sourceCollection   (optional) — informational
 *   size               "sm" | "xs"  default "xs"
 *   className          extra classes for the trigger button
 */
import React, { useState } from "react";
import axios from "axios";
import { Flag, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";

const TX = {
  label:   "Докладвай",
  title:   "Докладвай некоректна стойност",
  intro:   "Ако смятате, че тази стойност е грешна (напр. извън възможния диапазон), опишете какво не е наред. Администратор ще прегледа и коригира.",
  markerLbl: "Маркер",
  currentLbl:"Текуща стойност",
  correctedLbl: "Правилна стойност (по избор)",
  correctedUnitLbl: "Мерна единица (по избор)",
  reasonLbl: "Защо смятате, че е грешна?",
  reasonPh:  "Напр.: '85.7 µmol/L надвишава физиологичния максимум за DHEA-S (около 10 µmol/L)'",
  cancel:  "Отмени",
  submit:  "Изпрати доклад",
  submitted: "Докладът е изпратен — благодарим!",
  errMissing: "Моля, попълнете причината.",
  errServer: "Възникна грешка. Опитайте отново.",
};

export default function MarkerReportButton({
  markerCode,
  markerLabel = "",
  currentValue,
  currentUnit,
  labEntryId,
  sourceCollection,
  size = "xs",
  className = "",
}) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [correctedVal, setCorrectedVal] = useState("");
  const [correctedUnit, setCorrectedUnit] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error(TX.errMissing);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        marker_code:  markerCode,
        marker_label: markerLabel || undefined,
        current_value: (typeof currentValue === "number") ? currentValue : undefined,
        current_unit:  currentUnit || undefined,
        reason: reason.trim(),
        lab_entry_id: labEntryId || undefined,
        source_collection: sourceCollection || undefined,
      };
      if (correctedVal.trim() !== "") {
        const n = Number(correctedVal.replace(",", "."));
        if (!Number.isNaN(n)) payload.reported_value = n;
      }
      if (correctedUnit.trim()) payload.reported_unit = correctedUnit.trim();
      await axios.post(`${API}/marker-reports`, payload);
      setDone(true);
      toast.success(TX.submitted);
      setTimeout(() => {
        setOpen(false);
        setDone(false);
        setReason("");
        setCorrectedVal("");
        setCorrectedUnit("");
      }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || TX.errServer);
    } finally {
      setSubmitting(false);
    }
  };

  const btnSize = size === "sm"
    ? "px-2 py-1 text-xs"
    : "px-1.5 py-0.5 text-[10px]";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={TX.label}
        data-testid={`marker-report-btn-${markerCode}`}
        className={`inline-flex items-center gap-1 rounded-md font-medium transition-colors ${btnSize} ${
          isDark
            ? "text-red-300/70 hover:text-red-200 hover:bg-red-500/10"
            : "text-red-500/70 hover:text-red-600 hover:bg-red-50"
        } ${className}`}
      >
        <Flag className="w-3 h-3" />
        <span className="sr-only sm:not-sr-only">{TX.label}</span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => !submitting && setOpen(false)}
          data-testid="marker-report-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-md rounded-2xl p-5 shadow-xl ${
              isDark ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-200"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold">{TX.title}</h3>
              <button
                onClick={() => !submitting && setOpen(false)}
                className={`p-1 rounded-md ${isDark ? "hover:bg-slate-800" : "hover:bg-slate-100"}`}
                data-testid="marker-report-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className={`text-xs leading-relaxed mb-3 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {TX.intro}
            </p>

            <div className={`text-xs rounded-lg p-2.5 mb-3 ${isDark ? "bg-slate-800" : "bg-slate-50"}`}>
              <div><b>{TX.markerLbl}:</b> <span className="font-mono">{markerCode}</span>{markerLabel ? ` — ${markerLabel}` : ""}</div>
              {(currentValue !== undefined && currentValue !== null) && (
                <div className="mt-1">
                  <b>{TX.currentLbl}:</b> {currentValue}{currentUnit ? ` ${currentUnit}` : ""}
                </div>
              )}
            </div>

            <label className="block text-xs font-semibold mb-1">{TX.reasonLbl}</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={TX.reasonPh}
              rows={3}
              maxLength={1000}
              data-testid="marker-report-reason"
              className={`w-full text-xs rounded-lg p-2 mb-3 border ${
                isDark
                  ? "bg-slate-800 border-slate-700 text-slate-100"
                  : "bg-white border-slate-300 text-slate-800"
              }`}
            />

            <div className="grid grid-cols-2 gap-2 mb-4">
              <div>
                <label className="block text-[11px] font-semibold mb-1">{TX.correctedLbl}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={correctedVal}
                  onChange={(e) => setCorrectedVal(e.target.value)}
                  placeholder="напр. 8.4"
                  data-testid="marker-report-corrected-value"
                  className={`w-full text-xs rounded-lg p-2 border ${
                    isDark ? "bg-slate-800 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800"
                  }`}
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold mb-1">{TX.correctedUnitLbl}</label>
                <input
                  type="text"
                  value={correctedUnit}
                  onChange={(e) => setCorrectedUnit(e.target.value)}
                  placeholder="µmol/L"
                  data-testid="marker-report-corrected-unit"
                  className={`w-full text-xs rounded-lg p-2 border ${
                    isDark ? "bg-slate-800 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800"
                  }`}
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setOpen(false)}
                disabled={submitting}
                data-testid="marker-report-cancel"
                className={`px-3 py-2 rounded-lg text-xs font-semibold ${
                  isDark ? "bg-slate-800 hover:bg-slate-700 text-slate-200"
                         : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                }`}
              >
                {TX.cancel}
              </button>
              <button
                onClick={submit}
                disabled={submitting || done}
                data-testid="marker-report-submit"
                className="px-3 py-2 rounded-lg text-xs font-semibold bg-gradient-to-r from-pink-500 to-violet-600 text-white flex items-center gap-1.5 disabled:opacity-60"
              >
                {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
                {done ? "✓" : TX.submit}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
